import { useEffect, useMemo, useState } from 'react';
import { ApiError, type DevicePairingStart } from '@holdthat/api';
import { ArrowRight, Check, Cloud, KeyRound, LoaderCircle, LockKeyhole, Mail, QrCode, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, useAppData } from '../../state/AppData';
import { encodeQRCode } from './qrCode';

function PairingQRCode({ value }: { value: string }) {
  const modules = useMemo(() => encodeQRCode(value), [value]);
  const quietZone = 4;
  const size = modules.length + quietZone * 2;
  const path = modules.flatMap((row, y) => row.flatMap((dark, x) => (
    dark ? [`M${x + quietZone} ${y + quietZone}h1v1h-1z`] : []
  ))).join('');
  return (
    <svg className="pairing-qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="QR-Code zur Gerätefreigabe" shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#111118" />
    </svg>
  );
}

export function AuthScreen({ pendingPath }: { pendingPath: string }) {
  const navigate = useNavigate();
  const { authenticate } = useAppData();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pairing, setPairing] = useState<DevicePairingStart>();

  const finishAuthentication = async (session: Awaited<ReturnType<typeof api.completeDevicePairing>>) => {
    await authenticate(session);
    navigate(pendingPath.startsWith('/app/') ? pendingPath : '/app/inbox', { replace: true });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      if (!codeSent) { await api.startEmailAuth(email.trim()); setCodeSent(true); }
      else {
        const session = await api.completeEmailAuth(email.trim(), code, true);
        await finishAuthentication(session);
      }
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Die Anmeldung ist fehlgeschlagen.'); }
    finally { setBusy(false); }
  };

  const startPairing = async () => {
    setBusy(true); setError('');
    try {
      setPairing(await api.startDevicePairing(navigator.platform || 'Webbrowser'));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Der Gerätecode konnte nicht erstellt werden.');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!pairing) return;
    let active = true;
    let timer: number | undefined;
    const complete = async () => {
      if (!active) return;
      if (Date.now() >= new Date(pairing.expiresAt).getTime()) {
        setError('Der Gerätecode ist abgelaufen. Erstelle bitte einen neuen Code.');
        setPairing(undefined);
        return;
      }
      try {
        const session = await api.completeDevicePairing(pairing.pairingID, pairing.completionSecret, true);
        if (active) await finishAuthentication(session);
      } catch (cause) {
        if (!active) return;
        if (cause instanceof ApiError && cause.status === 409 && cause.code === 'pairing_pending') {
          timer = window.setTimeout(() => void complete(), 1500);
        } else {
          setError(cause instanceof Error ? cause.message : 'Die Gerätefreigabe ist fehlgeschlagen.');
          setPairing(undefined);
        }
      }
    };
    void complete();
    return () => { active = false; if (timer !== undefined) window.clearTimeout(timer); };
  // `finishAuthentication` intentionally reflects the current route and app context.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairing, authenticate, navigate, pendingPath]);

  const pairingURL = pairing
    ? `https://holdthat.de/device-pair?${new URLSearchParams({ pairingID: pairing.pairingID, approvalSecret: pairing.approvalSecret })}`
    : undefined;

  return (
    <main className="auth-layout">
      <section className="auth-story" aria-label="HoldThat Einführung">
        <div className="brand brand-light"><img className="brand-mark" src="/app/icons/icon-192.png" alt="" /><span>HoldThat</span></div>
        <div className="auth-copy">
          <p className="eyebrow">Alles Interessante. An einem Ort.</p>
          <h1>Heute gesehen.<br />Monate später gefunden.</h1>
          <p>Links, Ideen, Rezepte, Orte und Dateien aus jeder App festhalten – privat, schnell und jederzeit griffbereit.</p>
          <ul className="auth-benefits">
            <li><Check /> Alle Inhalte sofort durchsuchen</li>
            <li><Check /> Gemeinsam mit Familie und Freunden</li>
            <li><Check /> Offline verfügbar, ohne Werbung und Tracker</li>
          </ul>
        </div>
        <p className="auth-footnote"><LockKeyhole /> Deine privaten Holds werden nicht öffentlich indexiert.</p>
      </section>
      <section className="auth-panel">
        <div className="auth-form-wrap">
          <div className="brand brand-mobile"><img className="brand-mark" src="/app/icons/icon-192.png" alt="" /><span>HoldThat</span></div>
          <div className="auth-icon">{pairing ? <QrCode /> : <Mail />}</div>
          <p className="eyebrow">Willkommen zurück</p>
          {pairing && pairingURL ? <>
            <h2>Über die App verbinden</h2>
            <p className="muted">Öffne HoldThat auf deinem iPhone oder Android-Gerät, wähle in den <strong>Einstellungen „Gerät per QR-Code verbinden“</strong> und scanne diesen Code. Bestätige die Anmeldung anschließend in der App.</p>
            <PairingQRCode value={pairingURL} />
            <p className="pairing-wait"><LoaderCircle className="spin" /> Warte auf Freigabe …</p>
            {error && <p className="form-error" role="alert">{error}</p>}
            <button type="button" className="text-button" onClick={() => { setPairing(undefined); setError(''); }}>Abbrechen und per E-Mail anmelden</button>
          </> : <>
            <h2>{codeSent ? 'Code eingeben' : 'Mit E-Mail anmelden'}</h2>
            <p className="muted">{codeSent ? `Wir haben einen sechsstelligen Anmeldecode an ${email} gesendet.` : 'Melde dich mit deiner HoldThat-E-Mail-Adresse an. So bleiben deine Holds und Gruppen auf allen Geräten verbunden.'}</p>
            <form onSubmit={submit} className="stack-form">
              <label>E-Mail-Adresse<input autoFocus={!codeSent} type="email" autoComplete="email" value={email} disabled={codeSent || busy} onChange={(event) => setEmail(event.target.value)} placeholder="du@beispiel.de" required /></label>
              {codeSent && <label>Anmeldecode<input autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" pattern="\d{6}" required /></label>}
              {error && <p className="form-error" role="alert">{error}</p>}
              <button className="primary-button large" disabled={busy || (codeSent ? code.length !== 6 : !email.trim())}>{busy ? <LoaderCircle className="spin" /> : codeSent ? <KeyRound /> : <ArrowRight />}{busy ? 'Bitte warten …' : codeSent ? 'Sicher anmelden' : 'Code senden'}</button>
              {codeSent && <button type="button" className="text-button" onClick={() => { setCodeSent(false); setCode(''); setError(''); }}>Andere E-Mail-Adresse verwenden</button>}
            </form>
            {!codeSent && <div className="auth-alternative"><span>oder</span><button type="button" className="secondary-button full" disabled={busy} onClick={() => void startPairing()}>{busy ? <LoaderCircle className="spin" /> : <Smartphone />} Über die App verbinden</button></div>}
          </>}
          <div className="auth-security"><Cloud /><span><strong>Privacy First</strong> Deine Daten bleiben privat und werden verschlüsselt übertragen.</span></div>
        </div>
      </section>
    </main>
  );
}
